import {
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { StepState } from "./schema";

interface Props {
  steps: StepState[];
  deltaHtml: string;
  finalHtml: string | null;
}

const stepIconSx = { fontSize: 18, mr: 1 };

function StepIcon({ status }: { status: StepState["status"] }) {
  if (status === "done") {
    return (
      <Box
        component="i"
        className="fa-solid fa-circle-check"
        sx={{ ...stepIconSx, color: "#16a34a" }}
      />
    );
  }
  if (status === "error") {
    return (
      <Box
        component="i"
        className="fa-solid fa-circle-exclamation"
        sx={{ ...stepIconSx, color: "#c0392b" }}
      />
    );
  }
  if (status === "active") {
    return (
      <CircularProgress
        size={16}
        thickness={5}
        sx={{ mr: 1, color: "#8084FF" }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: "2px solid #d1d5dc",
        mr: 1,
        ml: "2px",
      }}
    />
  );
}

function StepRow({ step }: { step: StepState }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1,
        borderRadius: 1,
        background:
          step.status === "active" ? "rgba(128,132,255,0.10)" : "transparent",
      }}
    >
      <StepIcon status={step.status} />
      <Typography
        variant="body2"
        sx={{
          color: step.status === "pending" ? "text.secondary" : "text.primary",
          fontWeight: step.status === "active" ? 500 : 400,
        }}
      >
        {step.label}
      </Typography>
    </Box>
  );
}

export default function LoadingView({ steps, deltaHtml, finalHtml }: Props) {
  const draftStep = steps.find((s) => s.key === "draft");
  const enrichmentSteps = steps.filter((s) => s.key !== "draft");
  const draftStarted =
    draftStep?.status === "active" || draftStep?.status === "done";
  const allEnrichmentResolved =
    enrichmentSteps.length === 0 ||
    enrichmentSteps.every((s) => s.status === "done" || s.status === "error");
  const enrichmentCollapsed = draftStarted && allEnrichmentResolved;
  const erroredCount = enrichmentSteps.filter(
    (s) => s.status === "error",
  ).length;
  const html = finalHtml ?? deltaHtml;

  const [enrichmentExpanded, setEnrichmentExpanded] = useState(false);

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      {!enrichmentCollapsed && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Crafting your personalized draft
        </Typography>
      )}

      {enrichmentSteps.length > 0 && (
        <Box>
          {enrichmentCollapsed ? (
            <Box
              role="button"
              onClick={() => setEnrichmentExpanded((v) => !v)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                cursor: "pointer",
                background: "rgba(22,163,74,0.06)",
                "&:hover": { background: "rgba(22,163,74,0.1)" },
              }}
            >
              <Box
                component="i"
                className="fa-solid fa-circle-check"
                sx={{ color: "#16a34a", fontSize: 16 }}
              />
              <Typography
                variant="body2"
                sx={{ flex: 1, fontWeight: 500, color: "#0a0a0a" }}
              >
                {erroredCount > 0
                  ? `Setup complete (${enrichmentSteps.length - erroredCount}/${
                      enrichmentSteps.length
                    } steps)`
                  : `Setup complete (${enrichmentSteps.length} step${
                      enrichmentSteps.length === 1 ? "" : "s"
                    })`}
              </Typography>
              <Box
                component="i"
                className={
                  enrichmentExpanded
                    ? "fa-regular fa-chevron-up"
                    : "fa-regular fa-chevron-down"
                }
                sx={{ fontSize: 12, color: "text.secondary" }}
              />
            </Box>
          ) : (
            <Stack spacing={1}>
              {enrichmentSteps.map((s) => (
                <StepRow key={s.key} step={s} />
              ))}
            </Stack>
          )}

          <Collapse in={enrichmentCollapsed && enrichmentExpanded}>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {enrichmentSteps.map((s) => (
                <StepRow key={s.key} step={s} />
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      {draftStep && (!enrichmentCollapsed || !html) && (
        <StepRow step={draftStep} />
      )}

      {html && (
        <Box
          sx={{
            mt: 1,
            p: 2,
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 1,
            background: "white",
            fontSize: 14,
            lineHeight: 1.55,
            "& p": { m: 0 },
            "& p:empty::after": { content: '"\\00A0"' },
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </Stack>
  );
}
